/* eslint-disable vue/one-component-per-file */
/* eslint-disable no-param-reassign */

import moment from 'moment';
import _ from 'lodash';
import { defineComponent, createApp } from 'vue';
import Renderer from './core/render';
import Core from './core';
import constants from '$$helpers/constants';
import selectUI from './ui/select/select.vue';
import radioUI from './ui/radio/radio.vue';
import helpPopup from '../../help/helpPopup';
import { getPlans, getPlansObj, checkPricingAccess } from '$$helpers/pricingHelper';
import rights from '../../../components/rights';
import workload_block_icon from '../../../svg/workload_block.svg';
import arrow_left from '../../../svg/pricing/ic_arrow_left.svg';
import expandButtons from '../extensions/expandButtons';

class Layout {
  constructor() {
    this.GLOBAL_WORKLOAD_GRID_WIDTH = 300;
    this.ganttInstance = null;
    this.config = null;
    this.selectUI = null;

    this.handleResizeForWorkloadPanel = _.debounce(() => {
      const gantt = this.ganttInstance;
      const workloadHeight = gantt.$ui?.getView('workload').getNode().offsetHeight;
      const chartHeight = gantt.$ui?.getView('ganttChart').getNode().offsetHeight;

      if (!chartHeight || !workloadHeight) {
        return;
      }

      if (workloadHeight < constants.WORKLOAD_PANEL_MIN_HEIGHT && !gantt.config.hide_workload) {
        gantt.callEvent('onClickWorkloadExpandButton', []);
        gantt.callEvent('onChangeWorkloadHeight', [constants.WORKLOAD_PANEL_DEFAULT_HEIGHT]);
      }
      if (workloadHeight >= constants.WORKLOAD_PANEL_MIN_HEIGHT) {
        gantt.callEvent('onChangeWorkloadHeight', [workloadHeight]);
      }
    }, 200);
  }

  init(instance, config) {
    const workloadLayout = instance.$ui.getView('workload');
    const ganttLayout = instance.$ui.getView('main');
    const isWorkloadPanelType = config.type === constants.WORKLOAD_TYPES.panel;

    this.config = config;
    this.ganttInstance = instance;

    workloadLayout.$config.height = config.height;

    ganttLayout.attachEvent('onResize', () => {
      this.handleResizeForWorkloadPanel();
    });

    if (isWorkloadPanelType) {
      workloadLayout.$view.classList.add('panel');
      this.togglePanel(config.isShow);
    }

    if (!config.rightsAccess) {
      expandButtons.hideWorkloadButton();

      return;
    }

    if (!checkPricingAccess('workload')) {
      this._renderPricingPlaceHolder();

      return;
    }

    if (!isWorkloadPanelType) {
      workloadLayout.$config.config.scale_height = 38;
      this.ganttInstance.config.grid_width = this.GLOBAL_WORKLOAD_GRID_WIDTH; // hack to set grid width for workload grid
      this.ganttInstance.config.columns = [{
        width: this.GLOBAL_WORKLOAD_GRID_WIDTH,
      }];
      workloadLayout.resize();
      this.ganttInstance.$ui.getView('ganttChart').hide();
      this.ganttInstance.callEvent('setWorkloadViewMode', [this.config.viewMode]); // for global header view
    }

    this._initHeader();
    this._initRange();

    ganttLayout.attachEvent('onResize', () => {
      this.renderZoomHourLayout();
    });

    workloadLayout.resize();
  }

  getLayout(isBase, isExport) {
    return {
      id: 'workload',
      css: 'workload-body local',
      gravity: 1,
      config: {
        columns: [
          {
            name: 'workload_resource_name',
            label: __('workload_resource_name'),
          },
        ],
        scale_height: '0',
        row_height: 30,
        task_bar_height: 22,
      },
      rows: [
        {
          css: 'workload-header',
          id: 'workloadHeader',
          height: 48,
          min_height: 48,
          max_height: 48,
          cols: this._getHeaderTemplate(isBase, isExport),
        },
        this._getBodyLayoutConfig(),
      ],
    };
  }

  togglePanel(flag) {
    const workloadLayout = this.ganttInstance.$ui.getView('workload');
    const workloadHeader = this.ganttInstance.$ui.getView('workloadHeader');
    const workloadResizer = document.querySelector('.workload-resizer');

    workloadLayout[flag ? 'show' : 'hide']();
    workloadHeader[flag ? 'show' : 'hide']();

    flag && this._initHeader();

    workloadLayout.resize();
    workloadHeader.resize();

    this.ganttInstance.config.hide_workload = !flag;

    if (workloadResizer) {
      workloadResizer.parentNode.classList[flag ? 'remove' : 'add']('hide');
    }
  }

  refreshHeight() {
    if (!this.config || this.config.type === constants.WORKLOAD_TYPES.panel) {
      return;
    }

    const workloadLayout = this.ganttInstance.$ui.getView('workload');
    const scrollBarHeight = this.ganttInstance.$ui.getView('scrollHor').getNode().offsetHeight || 0;

    workloadLayout.$config.height = parseInt(this.ganttInstance.ganttDataApi.getWindowParams().height, 10);
    workloadLayout.$config.height = parseInt(this.ganttInstance.ganttDataApi.getWindowParams().height, 10) - scrollBarHeight;
    workloadLayout.resize();

    return true;
  }

  togglePanelAndRender(flag, height) {
    this.ganttInstance.$ui.getView('workload').$config.height = height;
    this.togglePanel(flag);
    if (flag && checkPricingAccess('workload')) {
      Core.calculateAndRender();
    }
  }

  changeRange(range) {
    const rangePicker = $$('workloadRange');

    this.config.range = range;

    const startDate = this.config.range.period ? moment(rangePicker.getPopup().getValue().start).toDate() : moment(this.config.range.start).startOf('day').toDate();
    const endDate = this.config.range.period ? moment(rangePicker.getPopup().getValue().end).toDate() : moment(this.config.range.end).add(1, 'day').toDate();

    this.ganttInstance.config.custom_start = startDate;
    this.ganttInstance.config.custom_end = endDate;
    this.ganttInstance.render();
    Core.clearCache();
    Core.calculateAndRender();
  }

  renderZoomHourLayout() {
    if (this.config.type !== constants.WORKLOAD_TYPES.panel) {
      return;
    }

    const isZoomHour = this.ganttInstance.config.durationData.mode === 'hour';

    const timelineView = this.ganttInstance.$ui.getView('workload');
    let layout = timelineView.$view.querySelector('.workload-fake');

    if (isZoomHour && !layout) {
      layout = document.createElement('div');
      layout.className = 'workload-fake';
      layout.innerHTML = `<div class='workload-fake-layout'>
          <div class='workload-fake-layout-top'>
            <div class='workload-fake-layout-photo'></div>
            <div class='workload-fake-layout-text'>${__('edit_is_not_possible')}</div>
          </div>
          <div class='workload-fake-layout-button'>${__('button_display_in_days')}</div>
        </div>`;

      layout.addEventListener('click', e => {
        if (e && e.target && e.target.closest('.workload-fake-layout-button')) {
          if (this.ganttInstance.ganttDataApi.isExport()) {
            this.ganttInstance.callEvent('export:workloadResetZoom', [1]);// day zoom
          } else {
            this.ganttInstance.callEvent('setZoom', [1]);
          }
        }
      });

      timelineView.$view.appendChild(layout);
    }

    if (layout) {
      const timelineSizes = timelineView.$view.getBoundingClientRectWrapper();
      const workloadGrid = this.ganttInstance.$ui.getView('workloadGrid').$grid.getBoundingClientRectWrapper();

      layout.style.cssText = [
        `top: ${timelineSizes.height - workloadGrid.height}px`,
        `left: ${workloadGrid.width + 1}px`,
        `width: ${timelineSizes.width - workloadGrid.width}px`,
        `height: ${workloadGrid.height}px`,
      ].join(';');

      layout.style.display = isZoomHour ? 'flex' : 'none';
    }
  }

  _getHeaderTemplate(isBase, isExport) {
    let headerLayout = [
      {
        html: `<div>${__('workload_header_title')}</div>${
          isExport ? '' : '<div class="workload_mode_wrap"></div>'}`,
        css: 'workload-header-title',
        group: 'grids',
      },
      { resizer: true, width: 1 },
      {
        view: 'template',
        css: 'workload-header-controls',
        borderless: true,
        html: '<div class="workload_group_by_wrap"></div>',
      },
      {
        view: 'template',
        id: 'workloadHelpBlock',
        css: 'help_icon_block',
        borderless: true,
        hidden: !isBase,
        width: 50,
        html:
          `<div class="help-icon" id='workloadHelpBlock'><div class="help-icon-inner"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n"
          '<path d="M12 22V18.9684C7 18.9684 4 15.2695 4 11C4 6.58737 7.5 3 12 3C16.4126 3 20 6 20 11C20 16 16 20 12 22ZM12 4C8 4 5 7 5 11C5 15 8.12458 18 12 18H13V20C16.0653 18.0632 19.0048 15 19 11C18.9952 7 16 4 12 4ZM11 14H13.0054L13 16H12.5H11V14ZM13 13H11C11 10.5 13 10.6842 13 9C13 8.44394 12.6568 8 12 8C11.5 8 11 8.5 11 9H9C9 7.13895 10.1389 6 12 6C14 6 15 7.5663 15 9C15 10.5 13 11.5 13 13Z" fill-rule="evenodd"/>\n'
          '</svg>\n</div></div>`,
      },
    ];

    if (!checkPricingAccess('workload')) {
      headerLayout = [{
        html: `<div>${__('workload_header_title')}</div>`,
        css: 'workload-header-title',
      }];
    }

    return headerLayout;
  }

  _getBodyLayoutConfig() {
    if (!checkPricingAccess('workload')) {
      return {};
    }

    return {
      cols: [
        {
          id: 'workloadGrid',
          view: 'resourceGrid',
          group: 'grids',
          scrollY: 'workloadVScroll',
          bind: 'workloadStore',
          layers: Renderer.getLayers().grid,
        },
        { resizer: false, width: 1, group: 'vertical' },
        {
          id: 'workloadTimeline',
          view: 'timeline',
          scrollX: 'scrollHor',
          scrollY: 'workloadVScroll',
          bind: 'workloadStore',
          layers: Renderer.getLayers().timeline,
        },
        {
          view: 'scrollbar', scroll: 'y', id: 'workloadVScroll', group: 'vertical',
        },
      ],
    };
  }

  _initHeader() {
    const workloadHeader = this.ganttInstance.$ui.getView('workloadHeader');
    const headerHelpIcon = workloadHeader.$view.querySelector('.help_icon_block .help-icon');
    const isWorkloadPanelType = this.config.type === constants.WORKLOAD_TYPES.panel;

    workloadHeader[isWorkloadPanelType ? 'show' : 'hide']();

    if (!isWorkloadPanelType) {
      return;
    }

    if (!this.ganttInstance.ganttDataApi.isExport()) {
      this._renderSelectUI();
    }
    this._renderRadioUI();

    if (headerHelpIcon) {
      headerHelpIcon.onclick = async () => {
        helpPopup.showHelpBlock('', 'workloadHelpBlock');

        const { activity_status } = await this.ganttInstance.ganttDataApi.updateUserActivityStatus({
          activityName: constants.USER_ACTIVITIES.HELP_WORKLOAD,
          status: 1,
        });

        headerHelpIcon.classList[activity_status === 1 ? 'add' : 'remove']('active-help');
      };
    }
  }

  _renderSelectUI() {
    const ComponentBuilderSelect = defineComponent(selectUI);

    const modeWrapper = document.getElementsByClassName('workload_mode_wrap')[0];

    if (this.selectUI) {
      this.selectUI.unmount(); // Vue 3 uses unmount() instead of $destroy()
    }

    // Create a new div element to mount the component
    const mountPoint = document.createElement('div');

    modeWrapper?.appendChild(mountPoint);

    this.selectUI = createApp(ComponentBuilderSelect, {
      options: [
        {
          title: __('workload_mode_all_projects_title'),
          value: 'allProjects',
        },
        {
          title: __('workload_mode_this_project_title'),
          value: 'scopedProjects',
        },
      ],
      value: this.config.calculationMode,
      ganttInstanceEventCaller: this.ganttInstance.callEvent,
    });

    this.selectUI.mount(mountPoint);
  }

  _renderRadioUI() {
    const ComponentBuilderRadio = defineComponent(radioUI);

    const groupByWrapper = document.getElementsByClassName('workload_group_by_wrap')[0];

    if (this.radioUI) {
      this.radioUI.unmount(); // Vue 3 uses unmount() instead of $destroy()
    }

    const radioMountPoint = document.createElement('div');

    groupByWrapper?.appendChild(radioMountPoint);

    // eslint-disable-next-line vue/one-component-per-file
    this.radioUI = createApp(ComponentBuilderRadio, {
      options: [
        {
          title: __('workload_header_hours_per_day'),
          value: constants.WORKLOAD_VIEW_MODES.hours,
        },
        {
          title: __('workload_header_percents_per_day'),
          value: constants.WORKLOAD_VIEW_MODES.percents,
        },
        {
          title: __('workload_header_tasks_per_day'),
          value: constants.WORKLOAD_VIEW_MODES.tasks,
        },
      ],
      value: this.config.viewMode,
      ganttInstanceEventCaller: this.ganttInstance.callEvent,
    });

    this.radioUI.mount(radioMountPoint);
  }

  _initRange() {
    const rangePicker = $$('workloadRange');

    if (!rangePicker || !rangePicker.isVisible()) {
      return;
    }

    if (this.config.range.period) {
      this.config.range.start = null;
      this.config.range.end = null;
    }

    rangePicker.blockEvent();
    rangePicker.setValue(this.config.range);
    rangePicker.unblockEvent();
    rangePicker.callEvent('afterUpdateValue');

    const startDate = this.config.range.period ? moment(rangePicker.getPopup().getValue().start).startOf('day').toDate() : moment(this.config.range.start).add(1, 'day').toDate();
    const endDate = this.config.range.period ? moment(rangePicker.getPopup().getValue().end).startOf('day').toDate() : moment(this.config.range.end).add(1, 'day').toDate();

    this.ganttInstance.config.custom_start = startDate;
    this.ganttInstance.config.custom_end = endDate;
    this.ganttInstance.render();
  }

  _renderPricingPlaceHolder() {
    const workloadView = this.ganttInstance.$ui.getView('workload');
    const upgradeBtn = (!rights.account.isOwner() || this.ganttInstance.ganttDataApi.isExport()) ? '' : `<div class="upgrade_button">${__('upgrade_rate_btn')}</div>`;
    const placeHolderNode = document.createElement('div');

    // if (!_state.workloadNoAccessPlaceHolderNode && !hasAccessToWorkLoad) {

    placeHolderNode.className = 'gantt_workload_placeholder';

    placeHolderNode.innerHTML = `<div class="placeholder_image">
        <img src="${GT.cdn}/imgs/workload/ganttchart_blocked_workload_left.png">
      </div>
      <div class="center_placeholder_part">
        ${workload_block_icon}
        <div class="description_block">
          <div class="text_wrapper">
            <span class="title">
              ${__('workload_access_title')}
              <span class="plan_label">${__(getPlans('workload'))}</span>
            </span>
            <div class="description">
              <div class="description-text">
              ${__('workload_access_desc')}
                <div class="learn_more">
                  <a class="pricing-popup-link" href="${__('workload_learn_more')}" target="_blank">${__('help_read_more')}</a>
                  <div class="arrow-left">${arrow_left}</div>
              </div>
              </div>
            </div>
          ${upgradeBtn}
        </div>
      </div>
      <div class="placeholder_image">
        <img src="${GT.cdn}/imgs/workload/ganttchart_blocked_workload_right.png">
      </div>`;

    const btnToPlan = placeHolderNode.querySelector('.upgrade_button');
    const linkClick = placeHolderNode.querySelector('.pricing-popup-link');
    const newPlanObj = getPlansObj('workload');

    btnToPlan && btnToPlan.addEventListener('click', () => {
      userExtAnalytics.log('pricing_deadlock_upgrade_clicked', { type: 'workload' });

      this.ganttInstance.callEvent('onShowUpgradePopup', [{
        show: true,
        mode: 'change-plan',
        newPlan: newPlanObj,
      }]);
    });

    linkClick && linkClick.addEventListener('click', () => {
      userExtAnalytics.log('pricing_deadlock_learn_more_clicked', { type: 'workload' });
    });

    workloadView.$view.appendChild(placeHolderNode);

    // if (_state.workloadNoAccessPlaceHolderNode) {
    //   _state.workloadNoAccessPlaceHolderNode.style.display = hasAccessToWorkLoad ? 'none' : 'flex';
    // }
  }
}

export default new Layout();
